import React from 'react';
import { ArrowDownLeft, Calendar, Filter, MoreVertical } from 'lucide-react';

interface ExecutiveSummaryReportProps {
  accounts: any[];
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  compareWith: string;
  setCompareWith: (value: string) => void;
  invoices: any[];
  bills: any[];
  setActiveReport: (report: string | null) => void;
}

export const ExecutiveSummaryReport: React.FC<ExecutiveSummaryReportProps> = ({
  accounts,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  compareWith,
  setCompareWith,
  invoices,
  bills,
  setActiveReport
}) => {
  const isWithinRange = (date: string) => {
    return date >= startDate && date <= endDate;
  };

  // Calculate metrics
  let cashReceived = 0;
  let cashSpent = 0;
  let totalIncome = 0;
  let directCosts = 0;
  let expenses = 0;

  invoices.forEach(inv => {
    if (isWithinRange(inv.date)) {
      if (inv.status === 'Paid') {
        cashReceived += inv.paymentReceived || inv.totalAmount;
      }
      if (inv.status !== 'Draft' && inv.status !== 'Void') {
        (inv.items || []).forEach((item: any) => {
          const account = accounts.find(a => a.id === item.accountId || a.code === item.accountId || a.name === item.accountName);
          if (account && account.type === 'Revenue') {
            totalIncome += item.total || (item.quantity * item.unitPrice);
          }
        });
      }
    }
  });

  bills.forEach(bill => {
    if (isWithinRange(bill.date)) {
      if (bill.status === 'Paid') {
        cashSpent += bill.paymentReceived || bill.totalAmount;
      }
      if (bill.status !== 'Draft' && bill.status !== 'Void') {
        (bill.items || []).forEach((item: any) => {
          const account = accounts.find(a => a.id === item.accountId || a.code === item.accountId || a.name === item.accountName);
          if (account) {
            const amount = item.total || (item.quantity * item.unitPrice);
            if (account.type === 'Direct Costs' || account.type === 'Cost of Sales') {
              directCosts += amount;
            } else if (account.type === 'Expense') {
              expenses += amount;
            }
          }
        });
      }
    }
  });

  const cashSurplus = cashReceived - cashSpent;
  const openingBankBalance = 1760.54;
  const closingBankBalance = openingBankBalance + cashSurplus;

  const grossProfit = totalIncome - directCosts;
  const netProfit = grossProfit - expenses;

  const debtors = invoices
    .filter(inv => inv.status === 'Awaiting Payment')
    .reduce((sum, inv) => sum + (inv.totalAmount - (inv.paymentReceived || 0)), 0);
  
  const creditors = bills
    .filter(bill => bill.status === 'Awaiting Payment')
    .reduce((sum, bill) => sum + (bill.totalAmount - (bill.paymentReceived || 0)), 0);

  const netAssets = debtors - creditors + closingBankBalance; // Simplified

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
            <span className="opacity-20">to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Compare with</span>
            <select 
              value={compareWith}
              onChange={(e) => setCompareWith(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value="None">None</option>
              <option value="1 month">1 month</option>
              <option value="2 months">2 months</option>
              <option value="3 months">3 months</option>
            </select>
          </div>

          <button className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all">
            <Filter className="w-4 h-4" />
            Filter
          </button>

          <button className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all">
            <MoreVertical className="w-4 h-4" />
            More
          </button>

          <button className="px-6 py-2 bg-[#141414] text-[#E4E3E0] rounded-full text-xs font-bold hover:opacity-90 transition-all">
            Update
          </button>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Executive Summary</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            Demo Company (Global)
          </p>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40 mt-1">
            For the period {new Date(startDate).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })} to {new Date(endDate).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414]">
                <th className="p-4 text-left text-[10px] uppercase font-bold tracking-widest opacity-40"></th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">{new Date(endDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Variance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5">
              <tr className="bg-[#141414]/5">
                <td colSpan={3} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">Cash</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Cash received</td>
                <td className="p-4 text-right font-mono text-xs">{cashReceived.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Cash spent</td>
                <td className="p-4 text-right font-mono text-xs">{cashSpent.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Cash surplus (deficit)</td>
                <td className="p-4 text-right font-mono text-xs">{cashSurplus.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="font-bold border-t border-[#141414]/20">
                <td className="p-4 text-xs">Closing bank balance</td>
                <td className="p-4 text-right font-mono text-xs text-primary">{closingBankBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>

              <tr className="bg-[#141414]/5">
                <td colSpan={3} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">Profitability</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Income</td>
                <td className="p-4 text-right font-mono text-xs">{totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Direct costs</td>
                <td className="p-4 text-right font-mono text-xs">{directCosts.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Gross profit (loss)</td>
                <td className="p-4 text-right font-mono text-xs">{grossProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Expenses</td>
                <td className="p-4 text-right font-mono text-xs">{expenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="font-bold border-t border-[#141414]/20">
                <td className="p-4 text-xs">Profit (loss)</td>
                <td className="p-4 text-right font-mono text-xs text-primary">{netProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>

              <tr className="bg-[#141414]/5">
                <td colSpan={3} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">Balance Sheet</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Debtors</td>
                <td className="p-4 text-right font-mono text-xs text-primary">{debtors.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Creditors</td>
                <td className="p-4 text-right font-mono text-xs text-primary">{creditors.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="font-bold border-t border-[#141414]/20">
                <td className="p-4 text-xs">Net assets</td>
                <td className="p-4 text-right font-mono text-xs text-primary">{netAssets.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
